import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { AnimatePresence, motion } from "framer-motion";
import {
    FaSyncAlt,
    FaSearch,
    FaChevronLeft,
    FaChevronRight,
    FaUsers,
    FaCheckCircle,
    FaTimesCircle,
    FaCopy,
    FaInfoCircle,
    FaEye,
    FaEyeSlash,
    FaEllipsisV,
    FaUser,
    FaPhone,
    FaEnvelope,
    FaLock,
    FaWallet,
    FaCoins,
    FaKey,
    FaUserCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CgProfile } from "react-icons/cg";
import { HiOutlineDocumentReport } from "react-icons/hi";

/* ---------------- Helpers ---------------- */
function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function isDemoUser(user) {
    const v = user?.is_demo;
    return v === true || v === 1 || v === "1";
}

function nfmt(v) {
    const num = Number(v || 0);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(num);
}

async function copyToClipboard(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_) { }

    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.left = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (_) {
        return false;
    }
}

function CopyBtn({ value, label = "Copied", disabled }) {
    const isDisabled =
        disabled || value === null || value === undefined || value === "" || value === "—";

    const onCopy = async () => {
        const ok = await copyToClipboard(String(value));
        if (ok) toast.success(label);
        else toast.error("Copy failed");
    };

    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary px-2 py-1"
            onClick={onCopy}
            disabled={isDisabled}
            title="Copy"
            style={{ fontSize: "0.75rem" }}
        >
            <FaCopy />
        </button>
    );
}

function LoadingRow() {
    return (
        <tr>
            {Array.from({ length: 9 }).map((_, i) => (
                <td key={i} className="px-2 py-2">
                    <div className="placeholder-glow">
                        <span className="placeholder col-10" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

/* -------------- Pagination (select) -------------- */
function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="text-muted small">
                Page <span className="fw-semibold">{pageNo}</span> of{" "}
                <span className="fw-semibold">{totalPages}</span>
            </div>

            <div className="d-flex align-items-center gap-2">
                <button
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft /> Prev
                </button>

                <button
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    Next <FaChevronRight />
                </button>

                <div className="d-flex align-items-center gap-2 ms-2">
                    <span className="text-muted small">Go to</span>
                    <select
                        className="form-select form-select-sm"
                        value={pageNo}
                        onChange={(e) => onJump(Number(e.target.value))}
                        disabled={loading || totalPages <= 1}
                        style={{ width: "70px", padding: "0.25rem 0.5rem" }}
                    >
                        {pages.map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}

/* ---------------- New Beautiful Modal ---------------- */
function InfoModal({ open, user, onClose }) {
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (open) setShowPassword(false);
    }, [open]);

    if (!user) return null;

    const id = user?.id;
    const name = safeText(user?.name);
    const username = safeText(user?.username);
    const cc = safeText(user?.country_code);
    const mobile = safeText(user?.mobile);
    const email = safeText(user?.email);
    const pwd = safeText(user?.password);

    const coinTotal = safeText(user?.balance?.coin?.total);
    const coinLock = safeText(user?.balance?.coin?.lock);
    const usdt = safeText(user?.balance?.usdt);

    const mobileDisplay = mobile === "—" ? "—" : `+${cc === "—" ? "" : cc} ${mobile}`.trim();

    const infoItems = [
        {
            icon: <FaUser className="text-primary" />,
            label: "User ID",
            value: id,
            copyable: true,
        },
        {
            icon: <FaUserCircle className="text-primary" />,
            label: "Full Name",
            value: name,
            copyable: false,
        },
        {
            icon: <FaPhone className="text-primary" />,
            label: "Mobile Number",
            value: mobileDisplay,
            copyable: true,
        },
        {
            icon: <FaEnvelope className="text-primary" />,
            label: "Email Address",
            value: email,
            copyable: true,
        },
    ];

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="modal-backdrop show d-block"
                        style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1040
                        }}
                    />

                    {/* Modal Container */}
                    <div
                        className="modal show d-block"
                        tabIndex="-1"
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 1050,
                            overflowY: "auto"
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: -20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: -20 }}
                            transition={{ duration: 0.2 }}
                            className="modal-dialog modal-dialog-centered modal-lg"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: "800px" }}
                        >
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                                {/* Modal Header */}
                                <div className="modal-header border-bottom-0 p-4" style={{ backgroundColor: "#f8f9fa", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
                                    <div className="d-flex align-items-center w-100">
                                        <div className="flex-grow-1">
                                            <h5 className="modal-title fw-bold text-dark mb-1 d-flex align-items-center gap-2 flex-wrap">
                                                <span>
                                                    <FaUserCircle className="me-2 text-primary" />
                                                    User Details
                                                </span>
                                                {isDemoUser(user) ? (
                                                    <span className="badge bg-secondary badge-compact">Demo</span>
                                                ) : null}
                                            </h5>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={onClose}
                                            aria-label="Close"
                                        />
                                    </div>
                                </div>

                                {/* Modal Body */}
                                <div className="modal-body p-0">
                                    <div className="p-4">
                                        {/* Basic Information Section */}
                                        <div className="mb-4">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center">
                                                <FaUser className="me-2 text-primary" />
                                                Basic Information
                                            </h6>
                                            <div className="row g-3">
                                                {infoItems.map((item, index) => (
                                                    <div key={index} className="col-12 col-md-6">
                                                        <div className="card border-0 bg-light h-100">
                                                            <div className="card-body p-3">
                                                                <div className="d-flex align-items-center mb-1">
                                                                    <div className="text-muted small flex-grow-1">
                                                                        {item.label}
                                                                    </div>
                                                                    {item.copyable && (
                                                                        <CopyBtn
                                                                            value={item.value === "—" ? "" : item.value}
                                                                            label={`${item.label} copied`}
                                                                            disabled={item.value === "—"}
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="d-flex align-items-center mt-2">
                                                                    {item.icon && <span className="me-2">{item.icon}</span>}
                                                                    <span className="fw-semibold text-dark" style={{
                                                                        wordBreak: "break-word",
                                                                        fontSize: "0.9rem"
                                                                    }}>
                                                                        {item.value}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Balance Information Section */}
                                        <div className="mb-4">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center">
                                                <FaWallet className="me-2 text-primary" />
                                                Balance Information
                                            </h6>
                                            <div className="row g-3">
                                                <div className="col-12 col-md-4">
                                                    <div className="card border-0 h-100" style={{ backgroundColor: "#e8f5e9" }}>
                                                        <div className="card-body p-3 text-center">
                                                            <div className="d-flex align-items-center justify-content-center mb-2">
                                                                <FaCoins className="text-success me-2" size={20} />
                                                                <div className="text-muted small">Coin Total</div>
                                                            </div>
                                                            <div className="h4 fw-bold text-success mb-0">{nfmt(coinTotal)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-md-4">
                                                    <div className="card border-0 h-100" style={{ backgroundColor: "#fff3cd" }}>
                                                        <div className="card-body p-3 text-center">
                                                            <div className="d-flex align-items-center justify-content-center mb-2">
                                                                <FaCoins className="text-warning me-2" size={20} />
                                                                <div className="text-muted small">Coin Lock</div>
                                                            </div>
                                                            <div className="h4 fw-bold text-warning mb-0">{nfmt(coinLock)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-md-4">
                                                    <div className="card border-0 h-100" style={{ backgroundColor: "#cff4fc" }}>
                                                        <div className="card-body p-3 text-center">
                                                            <div className="d-flex align-items-center justify-content-center mb-2">
                                                                <FaWallet className="text-info me-2" size={20} />
                                                                <div className="text-muted small">USDT Balance</div>
                                                            </div>
                                                            <div className="h4 fw-bold text-info mb-0">{nfmt(usdt)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Security Information Section */}
                                        <div className="mb-3">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center">
                                                <FaLock className="me-2 text-primary" />
                                                Security Information
                                            </h6>
                                            <div className="card border-0" style={{ backgroundColor: "#f8f9fa" }}>
                                                <div className="card-body p-4">
                                                    <div className="row align-items-center">
                                                        <div className="col-md-3">
                                                            <div className="d-flex align-items-center">
                                                                <FaKey className="text-primary me-2" />
                                                                <span className="fw-semibold text-dark">Password</span>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center">
                                                                {showPassword ? (
                                                                    <code className="bg-white p-2 rounded border flex-grow-1">
                                                                        {pwd}
                                                                    </code>
                                                                ) : (
                                                                    <div className="flex-grow-1">
                                                                        <div className="d-flex">
                                                                            <div className="bg-white rounded border p-2 d-flex align-items-center">
                                                                                <span className="text-muted">••••••••</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-secondary btn-sm px-3"
                                                                    onClick={() => setShowPassword((v) => !v)}
                                                                    title={showPassword ? "Hide password" : "Show password"}
                                                                >
                                                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                                    <span className="ms-2">{showPassword ? "Hide" : "Show"}</span>
                                                                </button>
                                                                {showPassword && (
                                                                    <CopyBtn
                                                                        value={pwd === "—" ? "" : pwd}
                                                                        label="Password copied"
                                                                        disabled={pwd === "—"}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Modal Footer */}
                                <div className="modal-footer border-top-0 p-4" style={{ backgroundColor: "#f8f9fa", borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px" }}>
                                    <div className="d-flex justify-content-end w-100">
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-secondary px-4"
                                                type="button"
                                                onClick={onClose}
                                            >
                                                Close
                                            </button>
                                            <button
                                                className="btn btn-primary px-4"
                                                type="button"
                                                onClick={() => {
                                                    navigate(`../user-profile/${username}`)
                                                }}
                                            >
                                                Profile
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

/* ---------------- Page ---------------- */
export default function Users() {
    const [loading, setLoading] = useState(true);

    const [inputText, setInputText] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");

    const [pageNo, setPageNo] = useState(1);
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({ total_user: 0, page_no: 1 });

    const [selectedUser, setSelectedUser] = useState(null);
    const [infoOpen, setInfoOpen] = useState(false);

    const navigate = useNavigate();
    const { logout } = useAuth();

    const PAGE_SIZE = 20;

    const totalPages = useMemo(() => {
        const total = Number(info?.total_user || 0);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / PAGE_SIZE));
    }, [info?.total_user]);



    const fetchUsers = useCallback(async ({ page, query }) => {
        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/user-list`,
                headers: { "Content-Type": "application/json" },
                data: {
                    page_no: page,
                    query: query || "",
                    token: localStorage.getItem("token") || "",
                },
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error == 'session expired') {

                    logout();
                } else {
                    toast.error(typeof data?.error === "string" ? data.error : "Failed to load users");
                    setList([]);
                    setInfo({ total_user: 0, page_no: page });
                    return;
                }
            }

            const raw = Array.isArray(data?.user_list) ? data.user_list : [];
            setList(raw);

            const apiInfo = data?.info || {};
            setInfo({
                total_user: Number(apiInfo?.total_user || 0),
                page_no: Number(apiInfo?.page_no || page),
            });
        } catch (err) {
            console.log(err);
            toast.error("Failed to load users");
            setList([]);
            setInfo({ total_user: 0, page_no: page });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers({ page: 1, query: "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchUsers({ page: pageNo, query: appliedQuery });
    }, [pageNo, appliedQuery, fetchUsers]);

    const onSearch = () => {
        const q = inputText.trim();
        setPageNo(1);
        setAppliedQuery(q);
        setInfoOpen(false);
        setSelectedUser(null);
    };

    const onRefreshReset = async () => {
        setInputText("");
        setAppliedQuery("");
        setPageNo(1);
        setInfoOpen(false);
        setSelectedUser(null);

        await fetchUsers({ page: 1, query: "" });
    };

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));

    const openInfo = (user) => {
        setSelectedUser(user);
        setInfoOpen(true);
    };
    const closeInfo = () => {
        setInfoOpen(false);
        setSelectedUser(null);
    };

    const actionToast = (label) => toast("Action: " + label);

    return (
        <PageTransition>
            <style>{`
                /* Table Styling */
                .users-table {
                    width: 100%;
                    margin-bottom: 0;
                    border-collapse: separate;
                    border-spacing: 0;
                }
                
                .users-table th {
                    background-color: var(--tb-table-head);
                    font-weight: 500;
                    text-transform: uppercase;
                    font-size: 0.68rem;
                    letter-spacing: 0.03em;
                    padding: 8px 6px;
                    border-bottom: 1px solid var(--tb-line);
                }
                
                .users-table td {
                    padding: 8px 6px;
                    vertical-align: middle;
                    border-bottom: 1px solid var(--tb-line);
                    font-size: 0.8125rem;
                }
                
                .users-table tbody tr:hover:not(.table-secondary) {
                    background-color: var(--tb-accent-soft);
                    transition: background-color 0.15s ease;
                }

                .users-table tbody tr.table-secondary:hover {
                    background-color: var(--tb-line-strong);
                }
                
                .users-table th:first-child {
                    border-top-left-radius: 4px;
                }
                
                .users-table th:last-child {
                    border-top-right-radius: 4px;
                }
                
                .users-table tbody tr:last-child td {
                    border-bottom: none;
                }
                
                /* Modal Styling */
                .modal-content {
                    border: 1px solid var(--tb-line);
                    box-shadow: var(--tb-shadow-lg);
                }
                
                .modal-header {
                    border-bottom: 1px solid var(--tb-line);
                }
                
                .modal-footer {
                    border-top: 1px solid var(--tb-line);
                }
                
                /* Card styling inside modal */
                .modal .card {
                    transition: none;
                }
                
                .modal .card:hover {
                    transform: none;
                    box-shadow: var(--tb-shadow);
                }
                
                /* Cell styling */
                .cell-name {
                    max-width: none;
                }
                
                .cell-mobile {
                    max-width: none;
                }
                
                .mono {
                    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
                    font-size: 0.8rem;
                }
                
                /* Badge styling */
                .badge-compact {
                    padding: 3px 7px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Table card */
                .table-card {
                    border-radius: 6px;
                    overflow: hidden;
                    box-shadow: var(--tb-shadow);
                }
                
                .table-responsive {
                    border-radius: 4px;
                }
                
                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .users-table th,
                    .users-table td {
                        padding: 10px 6px;
                        font-size: 0.8125rem;
                    }
                    
                    .cell-name,
                    .cell-mobile {
                        max-width: 120px;
                    }
                    
                    .modal-dialog {
                        margin: 1rem;
                    }
                }
                
                @media (max-width: 576px) {
                    .users-table th,
                    .users-table td {
                        padding: 8px 4px;
                    }
                    
                    .btn-sm {
                        padding: 0.25rem 0.5rem;
                        font-size: 0.75rem;
                    }
                    
                    .modal-dialog {
                        margin: 0.5rem;
                    }
                }
            `}</style>

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <div>
                    <h2 className="tb-page-title mb-1">Users Management</h2>
                    <div className="tb-page-subtitle mb-0">
                        Total Users: <span className="fw-semibold text-primary">{nfmt(info.total_user)}</span>
                        {appliedQuery ? (
                            <>
                                {" "}
                                • Search: <span className="fw-semibold text-info">{appliedQuery}</span>
                            </>
                        ) : null}
                    </div>
                </div>

                <button
                    className="btn btn-outline-primary btn-sm px-3 py-2 d-inline-flex align-items-center gap-2"
                    onClick={onRefreshReset}
                    disabled={loading}
                    type="button"
                    title="Reset & Refresh"
                >
                    <FaSyncAlt className={loading ? "tb-spin" : ""} />
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            {/* Search */}
            <div className="card shadow-sm border-0 mb-3">
                <div className="card-body p-3">
                    <div className="d-flex align-items-center gap-2 flex-nowrap">
                        <div className="input-group flex-grow-1">
                            <span className="input-group-text bg-light border-end-0">
                                <FaSearch className="text-muted" />
                            </span>
                            <input
                                className="form-control border-start-0"
                                placeholder="Search by name, mobile, email or ID..."
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") onSearch();
                                }}
                            />
                        </div>
                        <button
                            className="btn btn-primary px-3 d-inline-flex align-items-center gap-2 flex-shrink-0"
                            onClick={onSearch}
                            disabled={loading}
                            type="button"
                        >
                            <FaSearch /> Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card shadow-sm border-0 table-card">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table align-middle mb-0 users-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "44px" }}>#</th>
                                    <th style={{ width: "70px" }}>ID</th>
                                    <th>Name</th>
                                    <th>Mobile</th>
                                    <th className="text-center" style={{ width: "52px" }}>Status</th>
                                    <th className="text-end" style={{ width: "72px" }}>Coin</th>
                                    <th className="text-end" style={{ width: "64px" }}>Lock</th>
                                    <th className="text-end" style={{ width: "72px" }}>USDT</th>
                                    <th className="text-center" style={{ width: "52px" }}>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <>
                                        <LoadingRow />
                                        <LoadingRow />
                                        <LoadingRow />
                                        <LoadingRow />
                                        <LoadingRow />
                                    </>
                                ) : list.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-5">
                                            <div className="d-flex flex-column align-items-center gap-3">
                                                <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: 64,
                                                        height: 64,
                                                        background: "var(--tb-accent-soft)",
                                                        color: "var(--tb-accent)",
                                                    }}
                                                >
                                                    <FaUsers size={28} />
                                                </div>
                                                <div className="fw-semibold fs-5">No users found</div>
                                                <div className="text-muted">
                                                    {appliedQuery ? "Try a different search term" : "No users in the system"}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    list.map((u, index) => {
                                        const id = u?.id;
                                        const username = u?.username;
                                        const name = safeText(u?.name);
                                        const cc = safeText(u?.country_code);
                                        const mobile = safeText(u?.mobile);
                                        const mobileDisplay =
                                            mobile === "—" ? "—" : `+${cc === "—" ? "" : cc} ${mobile}`.trim();
                                        const serial = (pageNo - 1) * PAGE_SIZE + index + 1;

                                        const dropdownId = `user-actions-${id}`;

                                        return (
                                            <tr key={id} className={isDemoUser(u) ? "table-secondary" : undefined}>
                                                <td className="text-muted small">{serial}</td>

                                                <td className="fw-semibold">
                                                    <div className="d-flex align-items-center gap-1">
                                                        <span className="mono text-primary">{safeText(id)}</span>
                                                        <CopyBtn value={id} label="ID copied" />
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="cell-name" title={name}>
                                                        {name}
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="d-flex align-items-center gap-1">
                                                        <span className="cell-mobile" title={mobileDisplay}>
                                                            {mobileDisplay}
                                                        </span>
                                                        <CopyBtn value={mobile === "—" ? "" : mobile} label="Mobile copied" />
                                                    </div>
                                                </td>

                                                <td className="text-center">
                                                    {u?.status ? (
                                                        <span
                                                            className="badge bg-success badge-compact d-inline-flex align-items-center justify-content-center"
                                                            title="Active"
                                                            aria-label="Active"
                                                            style={{ width: 26, height: 26, padding: 0 }}
                                                        >
                                                            <FaCheckCircle />
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="badge bg-danger badge-compact d-inline-flex align-items-center justify-content-center"
                                                            title="Inactive"
                                                            aria-label="Inactive"
                                                            style={{ width: 26, height: 26, padding: 0 }}
                                                        >
                                                            <FaTimesCircle />
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="text-end small text-success" style={{ fontVariantNumeric: "tabular-nums" }}>
                                                    {nfmt(u?.balance?.coin?.total)}
                                                </td>
                                                <td className="text-end small text-warning" style={{ fontVariantNumeric: "tabular-nums" }}>
                                                    {nfmt(u?.balance?.coin?.lock)}
                                                </td>
                                                <td className="text-end small text-info" style={{ fontVariantNumeric: "tabular-nums" }}>
                                                    {nfmt(u?.balance?.usdt)}
                                                </td>

                                                <td className="text-center">
                                                    <div className="dropdown">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary px-2 py-1"
                                                            type="button"
                                                            id={dropdownId}
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded="false"
                                                            title="More actions"
                                                        >
                                                            <FaEllipsisV />
                                                        </button>

                                                        <ul className="dropdown-menu dropdown-menu-end shadow-sm" aria-labelledby={dropdownId}>
                                                            <li>
                                                                <button
                                                                    className="dropdown-item py-2 d-flex align-items-center gap-2"
                                                                    type="button"
                                                                    onClick={() => openInfo(u)}
                                                                >
                                                                    <FaInfoCircle /> Details
                                                                </button>
                                                            </li>
                                                            <li><hr className="dropdown-divider" /></li>
                                                            <li>
                                                                <button
                                                                    className="dropdown-item py-2 d-flex align-items-center gap-2"
                                                                    type="button"
                                                                    onClick={() => {
                                                                        navigate(`../user-profile/${username}`)
                                                                    }}
                                                                >
                                                                    <CgProfile /> Profile
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button
                                                                    className="dropdown-item py-2 d-flex align-items-center gap-2"
                                                                    type="button"
                                                                    onClick={() => {
                                                                        navigate(`../leader-stats/${username}`)
                                                                    }}
                                                                >
                                                                    <HiOutlineDocumentReport /> Subordinate
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button
                                                                    className="dropdown-item py-2 d-flex align-items-center gap-2"
                                                                    type="button"
                                                                    onClick={() => {
                                                                        navigate(`../user-downline-report/${username}`)
                                                                    }}
                                                                >
                                                                    <HiOutlineDocumentReport /> Report
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="border-top p-3 bg-light">
                        <PaginationBar
                            loading={loading}
                            pageNo={pageNo}
                            totalPages={totalPages}
                            onPrev={onPrev}
                            onNext={onNext}
                            onJump={onJump}
                        />
                    </div>
                </div>
            </div>

            <InfoModal open={infoOpen} user={selectedUser} onClose={closeInfo} />
        </PageTransition>
    );
}